import {
  EditBase,
  Form,
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRedirect,
} from "ra-core";
import { SaveButton } from "@/components/admin/form";
import { Card, CardContent } from "@/components/ui/card";
import { MeetingInputs } from "./MeetingInputs";
import { buildMeetingTasks } from "./meetingTasks";

type MeetingRecord = Record<string, any> & { id: string | number };

const MeetingEditForm = () => {
  const record = useRecordContext<MeetingRecord>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const relations = useGetList(
    "meeting_contacts",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "contact_id", order: "ASC" },
      filter: { meeting_id: record?.id },
    },
    { enabled: !!record?.id },
  );

  if (!record || relations.isPending) return null;

  const contactIds = (relations.data ?? []).map(
    (relation) => relation.contact_id,
  );
  const submit = async (values: Record<string, any>) => {
    const {
      contact_ids: nextContactIds = [],
      create_tasks_from_next_steps: createTasks,
      ...meeting
    } = values;
    try {
      await dataProvider.update("meetings", {
        id: record.id,
        data: meeting,
        previousData: record,
      });

      const next = new Set(nextContactIds as Array<string | number>);
      const previous = new Map(
        (relations.data ?? []).map((relation) => [
          relation.contact_id,
          relation,
        ]),
      );
      await Promise.all([
        ...[...next]
          .filter((contactId) => !previous.has(contactId))
          .map((contactId) =>
            dataProvider.create("meeting_contacts", {
              data: { meeting_id: record.id, contact_id: contactId },
            }),
          ),
        ...[...previous.entries()]
          .filter(([contactId]) => !next.has(contactId))
          .map(([, relation]) =>
            dataProvider.delete("meeting_contacts", {
              id: relation.id,
              previousData: relation,
            }),
          ),
      ]);

      if (createTasks) {
        await Promise.all(
          buildMeetingTasks(meeting, record.id).map((task) =>
            dataProvider.create("tasks", { data: task }),
          ),
        );
      }
      notify("Reunión actualizada", { type: "success" });
      redirect("list", "meetings");
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "No se pudo actualizar la reunión",
        { type: "error" },
      );
    }
  };

  return (
    <Card className="mx-auto mt-4 w-full max-w-4xl">
      <CardContent>
        <Form onSubmit={submit} record={{ ...record, contact_ids: contactIds }}>
          <MeetingInputs participants taskCreation />
          <div className="mt-6 flex justify-end">
            <SaveButton />
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export const MeetingEdit = () => (
  <EditBase resource="meetings">
    <MeetingEditForm />
  </EditBase>
);
